import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { PermissionAction, ModulePermission } from '../types/permissions';

/**
 * Hook for trader (customer) permission checks
 * Provides permission checking for trader-specific features
 */
export const useTraderPermissions = () => {
    const authContext = useContext(AuthContext);

    if (!authContext) {
        throw new Error('useTraderPermissions must be used within an AuthProvider');
    }

    const { user, isTrader } = authContext;

    // Type guard to check if user is a trader (customer)
    const isTraderUser = (user: any): user is import('../models/customer').Customer => {
        return user && isTrader();
    };

    const hasPermission = (module: ModulePermission, action: PermissionAction): boolean => {
        if (!isTraderUser(user) || !user?.permissions) return false;

        // Check if user has permissions for this module and action
        const modulePermissions = user?.permissions[module];

        if (!modulePermissions) {
            return false;
        }

        // Check if the action exists and is enabled
        const actionPermissions = modulePermissions[action as keyof typeof modulePermissions];

        if (!actionPermissions) {
            return false;
        }

        // If actionPermissions is an object with scope properties, check if any scope is enabled
        if (typeof actionPermissions === 'object' && actionPermissions !== null) {
            return (
                (actionPermissions as any).mine === true ||
                (actionPermissions as any).my_reports === true ||
                (actionPermissions as any).my_team === true ||
                (actionPermissions as any).all === true
            );
        }

        // Fallback to Boolean conversion
        return Boolean(actionPermissions);
    };

    const hasAnyPermission = (
        permissions: Array<{ module: ModulePermission; action: PermissionAction }>
    ): boolean => {
        return permissions.some(({ module, action }) => hasPermission(module, action));
    };

    const hasAllPermissions = (
        permissions: Array<{ module: ModulePermission; action: PermissionAction }>
    ): boolean => {
        return permissions.every(({ module, action }) => hasPermission(module, action));
    };

    const getPermissionDetails = (
        module: ModulePermission,
        action: PermissionAction
    ): { mine: boolean; my_reports: boolean; my_team: boolean; all: boolean } | null => {
        if (!isTraderUser(user) || !user?.permissions) return null;

        const modulePermissions = user?.permissions[module];

        if (!modulePermissions) {
            return null;
        }

        const actionPermissions = modulePermissions[action as keyof typeof modulePermissions];

        // Only return object types, not strings
        if (typeof actionPermissions === 'object' && actionPermissions !== null) {
            return {
                mine: (actionPermissions as any).mine === true,
                my_reports: (actionPermissions as any).my_reports === true,
                my_team: (actionPermissions as any).my_team === true,
                all: (actionPermissions as any).all === true,
            };
        }

        return null;
    };

    const getModulePermissions = (module: ModulePermission): Record<string, any> => {
        if (!isTraderUser(user) || !user?.permissions) return {};
        return user?.permissions[module] || {};
    };

    // Trader-specific permission checks
    const canViewOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.VIEW);
    const canCreateOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE);
    const canEditOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.EDIT);
    const canDeleteOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.DELETE);
    const canExportOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.EXPORT);

    const canViewTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.VIEW);
    const canCreateTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.CREATE);
    const canEditTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.EDIT);
    const canDeleteTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.DELETE);
    const canExportTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.EXPORT);

    const canViewPositions = () => hasPermission(ModulePermission.TRADING, PermissionAction.VIEW);
    const canCreatePositions = () =>
        hasPermission(ModulePermission.TRADING, PermissionAction.CREATE);
    const canEditPositions = () => hasPermission(ModulePermission.TRADING, PermissionAction.EDIT);
    const canDeletePositions = () =>
        hasPermission(ModulePermission.TRADING, PermissionAction.DELETE);

    const canViewTradingAccounts = () =>
        hasPermission(ModulePermission.TRADING_ACCOUNTS, PermissionAction.VIEW);
    const canEditTradingAccounts = () =>
        hasPermission(ModulePermission.TRADING_ACCOUNTS, PermissionAction.EDIT);

    const canViewTransactions = () =>
        hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.VIEW);
    const canEditTransactions = () =>
        hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.EDIT);

    // Permission groups for UI components
    const orderPermissions = {
        view: canViewOrders(),
        create: canCreateOrders(),
        edit: canEditOrders(),
        delete: canDeleteOrders(),
        export: canExportOrders(),
    };

    const tradePermissions = {
        view: canViewTrades(),
        create: canCreateTrades(),
        edit: canEditTrades(),
        delete: canDeleteTrades(),
        export: canExportTrades(),
    };

    const positionPermissions = {
        view: canViewPositions(),
        create: canCreatePositions(),
        edit: canEditPositions(),
        delete: canDeletePositions(),
    };

    const tradingAccountPermissions = {
        view: canViewTradingAccounts(),
        edit: canEditTradingAccounts(),
    };

    const transactionPermissions = {
        view: canViewTransactions(),
        edit: canEditTransactions(),
    };

    // Advanced permission checks
    const canPerformBulkOperations = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADES, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADING, action: PermissionAction.EDIT },
        ]);
    };

    const canExportTradingData = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADES, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADING, action: PermissionAction.EXPORT },
        ]);
    };

    const canViewTradingAnalytics = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADES, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADING, action: PermissionAction.VIEW },
        ]);
    };

    const canManageTrading = () => {
        return hasAllPermissions([
            { module: ModulePermission.ORDERS, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADES, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADING, action: PermissionAction.EDIT },
        ]);
    };

    return {
        // Core permission methods
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getPermissionDetails,
        getModulePermissions,

        // User type checks
        isTrader: isTraderUser(user),

        // Individual permission checks
        canViewOrders,
        canCreateOrders,
        canEditOrders,
        canDeleteOrders,
        canExportOrders,
        canViewTrades,
        canCreateTrades,
        canEditTrades,
        canDeleteTrades,
        canExportTrades,
        canViewPositions,
        canCreatePositions,
        canEditPositions,
        canDeletePositions,
        canViewTradingAccounts,
        canEditTradingAccounts,
        canViewTransactions,
        canEditTransactions,

        // Permission groups
        orderPermissions,
        tradePermissions,
        positionPermissions,
        tradingAccountPermissions,
        transactionPermissions,

        // Advanced checks
        canPerformBulkOperations,
        canExportTradingData,
        canViewTradingAnalytics,
        canManageTrading,
    };
};
